import { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Chronicle, ERAS, REGIONS } from "@/data/chronicles";
import { GoldRule } from "@/components/atlas/AtlasComponents";

interface TimelineProps {
  chronicles: Chronicle[];
  onChronicleSelect: (chronicle: Chronicle) => void;
  selectedRegion: string | null;
  /**
   * Region currently being highlighted — either by hovering a chronicle bar
   * in this timeline or by the map forwarding a region-pin hover. When set,
   * non-matching bars dim to show the focused region clearly.
   */
  hoveredRegion?: string | null;
  /**
   * Fired when a chronicle bar's hover state changes. Index.tsx lifts this
   * to also pulse the matching region pin on the map. Undefined disables
   * the cross-component sync (e.g. on touch devices).
   */
  onRegionHoverChange?: (regionId: string | null) => void;
}

const TIMELINE_START = 476;
const TIMELINE_END = 2026;
const PX_PER_YEAR = 3;
const TOTAL_WIDTH = (TIMELINE_END - TIMELINE_START) * PX_PER_YEAR;
const ROW_HEIGHT = 30;
const BASE_TOP = 56; // extra room for era labels
const BOTTOM_BUFFER = 56; // century numerals + half-century ticks + anchor labels
const POPUP_WIDTH = 196;
const POPUP_HEIGHT = 64;
const EDGE_MARGIN = 8;

/**
 * Famous historical anchors — annotations marking pivot dates that don't
 * have their own chronicle bar but help the player place known events
 * relative to the chronicles they're scanning. Pure ornament, no
 * interaction. Keep the list short (~10) so the chart stays readable;
 * each addition costs a small horizontal-real-estate budget.
 *
 * Years chosen to spread roughly evenly across the 1550-year span and
 * to land on universally-recognised pivots — so a curious player who
 * doesn't know a chronicle still sees touchstones like "Hastings",
 * "Reformation", or "the Wall falls" as they scroll.
 */
const TIMELINE_ANCHORS: ReadonlyArray<{ year: number; label: string }> = [
  { year: 1066, label: "Hastings" },
  { year: 1215, label: "Magna Carta" },
  { year: 1453, label: "Constantinople falls" },
  { year: 1492, label: "Atlantic crossing" },
  { year: 1517, label: "Reformation" },
  { year: 1648, label: "Westphalia" },
  { year: 1789, label: "Bastille" },
  { year: 1815, label: "Vienna" },
  { year: 1914, label: "Great War" },
  { year: 1945, label: "VE Day" },
  { year: 1989, label: "The Wall" },
];

/**
 * Inject an alpha channel into an `hsl(h, s%, l%)` string. The REGIONS table
 * stores colors in the comma-separated form (e.g. `hsl(43, 72%, 55%)`), so a
 * naive `replace(")", " / 0.5)")` is enough — but encapsulated here so the
 * timeline doesn't pepper the literal everywhere.
 */
function withAlpha(hsl: string, alpha: number): string {
  return hsl.replace(/\)$/, ` / ${alpha})`);
}

function assignRows(chronicles: Chronicle[]): Map<string, number> {
  const sorted = [...chronicles].sort((a, b) => a.dateRange[0] - b.dateRange[0]);
  const rowEndYears: number[] = [];
  const rowMap = new Map<string, number>();

  for (const chronicle of sorted) {
    const [start, end] = chronicle.dateRange;
    let assignedRow = -1;
    for (let r = 0; r < rowEndYears.length; r++) {
      if (rowEndYears[r] < start) {
        assignedRow = r;
        break;
      }
    }
    if (assignedRow === -1) {
      assignedRow = rowEndYears.length;
      rowEndYears.push(end);
    } else {
      rowEndYears[assignedRow] = end;
    }
    rowMap.set(chronicle.id, assignedRow);
  }

  return rowMap;
}

function ChroniclePopup({
  chronicle,
  barLeft,
  barTop,
  barWidth,
  scrollContainerRef,
}: {
  chronicle: Chronicle;
  barLeft: number;
  barTop: number;
  barWidth: number;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}) {
  // Compute position so popup stays within scrollable viewport
  const style = useMemo(() => {
    const container = scrollContainerRef.current;
    const scrollLeft = container?.scrollLeft ?? 0;
    const viewportWidth = container?.clientWidth ?? 800;

    // Horizontal: try aligning left edge with bar, clamp to viewport
    let left = 0;
    const barScreenX = barLeft - scrollLeft;
    if (barScreenX + POPUP_WIDTH > viewportWidth - EDGE_MARGIN) {
      // Align to right edge of bar instead
      left = barLeft + barWidth - POPUP_WIDTH;
      // Still clamp
      const rightScreenX = left - scrollLeft;
      if (rightScreenX < EDGE_MARGIN) {
        left = scrollLeft + EDGE_MARGIN;
      }
    } else {
      left = barLeft;
      if (barScreenX < EDGE_MARGIN) {
        left = scrollLeft + EDGE_MARGIN;
      }
    }

    // Vertical: prefer above the bar, flip below if near top
    let top: number;
    let openAbove = true;
    if (barTop < POPUP_HEIGHT + 12) {
      openAbove = false;
      top = barTop + 28;
    } else {
      top = barTop - POPUP_HEIGHT - 6;
    }

    return { left, top, openAbove };
  }, [barLeft, barTop, barWidth, scrollContainerRef]);

  return (
    <motion.div
      initial={{ opacity: 0, y: style.openAbove ? 3 : -3 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className="absolute z-20 rounded-sm px-2.5 py-1.5 pointer-events-none"
      style={{
        left: style.left,
        top: style.top,
        width: POPUP_WIDTH,
        background: "var(--surface-parchment-elevated)",
        border: "1px solid hsl(43, 50%, 35% / 0.4)",
        boxShadow: "0 4px 14px hsl(var(--ink) / 0.55), 0 0 0 1px hsl(var(--ink) / 0.2)",
      }}
    >
      {/* Gold hairline along top edge — echoes the map cartouche treatment. */}
      <GoldRule
        intensity="soft"
        className="absolute left-2 right-2"
        style={{ top: 0 }}
      />
      <p className="text-[11px] font-display text-gold leading-tight tracking-wide">
        {chronicle.title}
      </p>
      <p className="text-[9px] text-muted-foreground/80 font-body italic mt-0.5">
        {chronicle.dateRange[0]}–{chronicle.dateRange[1]}
      </p>
      <p className="text-[9px] text-foreground/55 font-body mt-0.5 leading-snug truncate">
        {chronicle.subtitle}
      </p>
    </motion.div>
  );
}

export default function Timeline({ chronicles, onChronicleSelect, selectedRegion, hoveredRegion = null, onRegionHoverChange }: TimelineProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hoveredChronicle, setHoveredChronicle] = useState<string | null>(null);
  const [showOriginTooltip, setShowOriginTooltip] = useState(false);
  const [, setScrollProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  // Tap-to-pin region focus from the legend — gives touch devices a
  // working equivalent of desktop's hover-to-focus, since pointer
  // hover events don't latch on touchscreens. Pinned takes precedence
  // over hovered; tapping an already-pinned legend entry clears it.
  const [pinnedRegion, setPinnedRegion] = useState<string | null>(null);
  const focusedRegion = pinnedRegion ?? hoveredRegion;

  // Sync slider with scroll position
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const maxScroll = el.scrollWidth - el.clientWidth;
      setScrollProgress(maxScroll > 0 ? el.scrollLeft / maxScroll : 0);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // ── Drag-to-scroll ──
  // Lets the player drag the timeline left/right with mouse OR touch in
  // addition to the existing native scrollbar / chevron buttons. Tracks
  // a small hysteresis (5 px) so a click on a chronicle bar still opens
  // it; only when the pointer moves past that threshold do we promote
  // the gesture to a drag and suppress the would-be click.
  const dragRef = useRef({
    pressed: false,
    moved: false,
    startX: 0,
    startScrollLeft: 0,
    pointerId: -1,
  });

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    // Mouse: only the primary button starts a drag. A finger is left to the
    // browser's own swipe scrolling (with its momentum): driving scrollLeft
    // by hand as well made the strip fight the finger and stutter on phones.
    if (e.pointerType === "touch") return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    dragRef.current = {
      pressed: true,
      moved: false,
      startX: e.clientX,
      startScrollLeft: el.scrollLeft,
      pointerId: e.pointerId,
    };
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    const el = scrollRef.current;
    if (!d.pressed || !el || e.pointerId !== d.pointerId) return;
    const dx = e.clientX - d.startX;
    if (!d.moved && Math.abs(dx) > 5) {
      d.moved = true;
      setIsDragging(true);
      // Capture so we keep getting move/up even if the pointer drifts
      // off the scroll container (over a sibling, the page edge, etc.).
      try { (e.target as Element).setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
    }
    if (d.moved) {
      el.scrollLeft = d.startScrollLeft - dx;
    }
  }, []);

  const handlePointerEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d.pressed) return;
    d.pressed = false;
    if (d.moved) {
      setIsDragging(false);
      try { (e.target as Element).releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
    }
    // d.moved stays true here — the click-capture handler below clears
    // it after consuming the drag-induced click on a chronicle bar.
  }, []);

  // Capture-phase click suppressor — when the pointer dragged past the
  // 5 px threshold, swallow the click that would otherwise fire on
  // whichever chronicle bar happened to be under the cursor at release.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onClickCapture = (e: MouseEvent) => {
      const d = dragRef.current;
      if (d.moved) {
        e.stopPropagation();
        e.preventDefault();
        d.moved = false;
      }
    };
    el.addEventListener("click", onClickCapture, true);
    return () => el.removeEventListener("click", onClickCapture, true);
  }, []);

  // Never keep hover state across context changes
  useEffect(() => {
    setHoveredChronicle(null);
    setPinnedRegion(null);
  }, [selectedRegion]);

  const filtered = selectedRegion
    ? chronicles.filter((c) => c.regionId === selectedRegion)
    : chronicles;

  const rowAssignments = useMemo(() => assignRows(filtered), [filtered]);
  const maxRow = useMemo(() => {
    let max = 0;
    rowAssignments.forEach((row) => {
      if (row > max) max = row;
    });
    return max;
  }, [rowAssignments]);

  const dynamicHeight = BASE_TOP + (maxRow + 1) * ROW_HEIGHT + BOTTOM_BUFFER;

  const yearToX = useCallback((year: number) => (year - TIMELINE_START) * PX_PER_YEAR, []);

  const scroll = (direction: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: direction === "left" ? -400 : 400,
      behavior: "smooth",
    });
  };

  return (
    <div className="w-full overflow-hidden">
      {/* ── Header: cartographer's label + brass rule + engraved scroll buttons ── */}
      <div className="flex items-center mb-3 px-1 gap-2 sm:gap-3 min-w-0">
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Tiny cardinal-points glyph — visual rhyme with the map's compass rose. */}
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true" className="flex-shrink-0">
            <line x1="6" y1="1" x2="6" y2="11" stroke="hsl(43, 50%, 50%)" strokeWidth="0.6" />
            <line x1="1" y1="6" x2="11" y2="6" stroke="hsl(43, 50%, 50%)" strokeWidth="0.4" />
            <circle cx="6" cy="6" r="1.2" fill="hsl(43, 72%, 55%)" />
          </svg>
          <h3 className="font-display text-xs sm:text-sm uppercase tracking-[0.2em] sm:tracking-[0.25em]" style={{ color: "hsl(var(--gold-dim))" }}>
            Timeline
          </h3>
        </div>
        {/* Brass rule sweeps between label and scroll controls. */}
        <div className="flex-1 min-w-0 mx-2 h-px brass-rule-dim" />
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          <button
            onClick={() => scroll("left")}
            aria-label="Scroll timeline earlier"
            className="nav-brass-btn p-1 rounded-sm border text-muted-foreground hover:text-gold transition-colors"
            style={{ borderColor: "hsl(43, 50%, 35% / 0.35)" }}
          >
            <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
          <span className="text-[10px] sm:text-xs font-body italic whitespace-nowrap" style={{ color: "hsl(var(--gold-dim) / 0.85)" }}>
            476 AD: Present
          </span>
          <button
            onClick={() => scroll("right")}
            aria-label="Scroll timeline later"
            className="nav-brass-btn p-1 rounded-sm border text-muted-foreground hover:text-gold transition-colors"
            style={{ borderColor: "hsl(43, 50%, 35% / 0.35)" }}
          >
            <ChevronRight className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          </button>
        </div>
      </div>

      {/* ── Region legend ── colours-mean-countries key, between header
            and the scroll body. Kept very subtle: 6 px swatch + small
            italic label per region, muted brass-on-parchment palette,
            wraps gracefully on narrow viewports. Filters to regions
            that actually appear in the current chronicle list so it
            responds to the selectedRegion filter.
            Hovering a legend entry calls onRegionHoverChange so the
            timeline bars dim non-matching regions exactly like a
            hover on a bar or a map region pin would — the legend
            participates in the same cross-component highlight. */}
      {(() => {
        const activeRegionIds = new Set(chronicles.map((c) => c.regionId));
        const legendRegions = (Object.values(REGIONS) as Array<{ id: string; name: string; color: string }>)
          .filter((r) => activeRegionIds.has(r.id));
        if (legendRegions.length === 0) return null;
        return (
          <div
            data-tutorial="timeline-filters"
            className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 mb-2"
            style={{ opacity: 0.7 }}
            aria-label="Region colour legend"
          >
            {legendRegions.map((r) => {
              // Dim non-matching legend entries whenever the user is
              // focused on a single region — either pinning one by
              // tap (touch), hovering one anywhere (legend / bar /
              // map), or having selected it via the filter controls.
              const filterMatch = selectedRegion === null || selectedRegion === r.id;
              const focusMatch = focusedRegion === null || focusedRegion === r.id;
              const isActive = filterMatch && focusMatch;
              const isPinned = pinnedRegion === r.id;
              return (
                <button
                  data-tutorial={r.id === "hre" ? "timeline-filter-central-europe" : undefined}
                  key={r.id}
                  type="button"
                  className="flex items-center gap-1.5 cursor-pointer bg-transparent border-0 p-0 m-0"
                  style={{
                    opacity: isActive ? 1 : 0.35,
                    transition: "opacity 0.18s ease-out",
                  }}
                  onPointerEnter={() => onRegionHoverChange?.(r.id)}
                  onPointerLeave={() => onRegionHoverChange?.(null)}
                  onClick={() => {
                    const next = isPinned ? null : r.id;
                    setPinnedRegion(next);
                    onRegionHoverChange?.(next);
                  }}
                  aria-pressed={isPinned}
                  aria-label={`Focus ${r.name}`}
                >
                  <span
                    aria-hidden="true"
                    style={{
                      display: "inline-block",
                      width: isPinned ? 8 : 6,
                      height: isPinned ? 8 : 6,
                      borderRadius: 9999,
                      background: r.color,
                      boxShadow: `0 0 ${isPinned ? 6 : 4}px ${withAlpha(r.color, isPinned ? 0.7 : 0.45)}`,
                    }}
                  />
                  <span
                    className="text-[9px] font-body italic whitespace-nowrap tracking-wide"
                    style={{
                      color: isPinned ? "hsl(var(--gold))" : "hsl(var(--gold-dim) / 0.7)",
                      borderBottom: isPinned ? "1px solid hsl(var(--gold) / 0.6)" : "1px solid transparent",
                    }}
                  >
                    {r.name}
                  </span>
                </button>
              );
            })}
          </div>
        );
      })()}

      {/* ── Body: scroll container framed as a folded chart ──
          Both native scrolling (touch swipe + mouse wheel + scrollbar)
          AND drag-to-scroll work in parallel. The chevron buttons in
          the header above are still a third option for keyboard /
          power users who prefer click-to-scroll. */}
      <div
        ref={scrollRef}
        data-tutorial="timeline-scroller"
        className={`overflow-x-auto overflow-y-visible scrollbar-thin pb-2 rounded-sm select-none ${isDragging ? "cursor-grabbing" : "cursor-grab"}`}
        style={{
          scrollbarColor: "hsl(43 50% 35% / 0.45) transparent",
          // A sideways swipe that reaches either end stays in the strip
          // instead of turning into the browser's back/forward gesture.
          overscrollBehaviorX: "contain",
          WebkitOverflowScrolling: "touch",
          border: "1px solid hsl(43, 50%, 35% / 0.2)",
          boxShadow: "inset 0 1px 0 hsl(43, 50%, 50% / 0.08), inset 0 -1px 0 hsl(var(--ink) / 0.3)",
        }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onPointerLeave={() => {
          setHoveredChronicle(null);
          onRegionHoverChange?.(null);
        }}
      >
        <div
          className="relative"
          style={{
            width: TOTAL_WIDTH,
            height: dynamicHeight,
            background: "var(--surface-parchment)",
          }}
        >
          {/* ── 476 AD Origin Medallion — brass roundel echoing the map's compass. ── */}
          <div
            className="absolute flex flex-col items-center z-10 cursor-default"
            style={{ left: 4, top: 36, width: 52 }}
            onMouseEnter={() => setShowOriginTooltip(true)}
            onMouseLeave={() => setShowOriginTooltip(false)}
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center"
              style={{
                background: "radial-gradient(circle at 30% 30%, hsl(43, 55%, 35%), hsl(35, 35%, 18%) 80%)",
                border: "1px solid hsl(43, 60%, 45% / 0.6)",
                boxShadow: "0 1px 2px hsl(var(--ink) / 0.5), inset 0 0 4px hsl(43, 80%, 65% / 0.15)",
              }}
            >
              <span className="text-[8px] font-display text-gold/90 tracking-tight leading-none">476</span>
            </div>
            <span className="text-[8px] font-display mt-1 uppercase tracking-wider whitespace-nowrap" style={{ color: "hsl(var(--gold-dim) / 0.7)" }}>
              AD
            </span>

            {showOriginTooltip && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute top-0 left-full ml-2 z-30 rounded-sm p-2"
                style={{
                  width: 208,
                  background: "var(--surface-parchment-elevated)",
                  border: "1px solid hsl(43, 50%, 35% / 0.4)",
                  boxShadow: "0 4px 14px hsl(var(--ink) / 0.55)",
                }}
              >
                <GoldRule intensity="soft" className="absolute left-2 right-2" style={{ top: 0 }} />
                <p className="text-[10px] font-display text-gold tracking-wide">Fall of the Western Roman Empire</p>
                <p className="text-[9px] text-muted-foreground/80 mt-0.5 leading-relaxed font-body">
                  Marks the beginning of the post-Roman era and the historical starting point of the platform timeline.
                </p>
              </motion.div>
            )}
          </div>

          {/* ── Era backgrounds — alternating parchment shade with engraved labels. ── */}
          {ERAS.map((era, index) => {
            const eraLeft = yearToX(era.start);
            const eraWidth = (era.end - era.start) * PX_PER_YEAR;
            // Alternate rows to prevent overlap between adjacent eras
            const labelTop = index % 2 === 0 ? 4 : 18;
            return (
              <div
                key={era.label}
                className="absolute top-0"
                style={{
                  left: eraLeft,
                  width: eraWidth,
                  height: "100%",
                  // Faint alternating wash so eras read as distinct spans of parchment.
                  background: index % 2 === 0 ? "transparent" : "hsl(38, 22%, 16% / 0.35)",
                  borderLeft: "1px solid hsl(43, 50%, 35% / 0.18)",
                }}
              >
                <span
                  className="absolute left-2 text-[9px] font-display uppercase tracking-[0.18em] whitespace-nowrap"
                  style={{ top: labelTop, color: "hsl(var(--gold-dim) / 0.7)" }}
                >
                  {era.label}
                  <span className="font-body italic normal-case ml-1.5 tracking-normal" style={{ color: "hsl(var(--gold-dim) / 0.4)" }}>
                    {era.start}–{era.end}
                  </span>
                </span>
              </div>
            );
          })}

          {/* ── Century markers — anchored to round centuries (500, 600, …, 2000)
                rather than offset from the 476 origin. The 476 medallion handles
                the start of the chart visually; from there the player reads
                evenly-spaced century numerals on the round-hundred grid. ── */}
          {(() => {
            const firstCentury = Math.ceil((TIMELINE_START + 1) / 100) * 100; // 500
            const years: number[] = [];
            for (let y = firstCentury; y <= TIMELINE_END; y += 100) years.push(y);
            return years.map((year) => {
              const x = yearToX(year);
              return (
                <div key={`c-${year}`}>
                  {/* Top brass tick */}
                  <div
                    className="absolute"
                    style={{
                      left: x,
                      top: 32,
                      width: 1,
                      height: 4,
                      background: "hsl(43, 50%, 45% / 0.4)",
                    }}
                  />
                  {/* Bottom brass tick + numeral */}
                  <div
                    className="absolute"
                    style={{
                      left: x,
                      bottom: 18,
                      width: 1,
                      height: 5,
                      background: "hsl(43, 50%, 45% / 0.45)",
                    }}
                  />
                  <span
                    className="absolute text-[9px] font-body"
                    style={{
                      left: x + 3,
                      bottom: 4,
                      color: "hsl(var(--gold-dim) / 0.55)",
                    }}
                  >
                    {year}
                  </span>
                </div>
              );
            });
          })()}

          {/* ── Half-century minor ticks — aligned to the round-century grid
                (550, 650, …, 1950) so they sit cleanly between centuries. ── */}
          {(() => {
            const firstCentury = Math.ceil((TIMELINE_START + 1) / 100) * 100; // 500
            const years: number[] = [];
            for (let y = firstCentury + 50; y <= TIMELINE_END; y += 100) years.push(y);
            return years.map((year) => {
              const x = yearToX(year);
              return (
                <div
                  key={`hc-${year}`}
                  className="absolute"
                  style={{
                    left: x,
                    bottom: 18,
                    width: 1,
                    height: 3,
                    background: "hsl(43, 50%, 45% / 0.22)",
                  }}
                />
              );
            });
          })()}

          {/* ── Decorative anchors — italic touchstones for famous pivot dates,
                placed in the bottom buffer above the century numerals. Each
                anchor is a brass drop-tick + diamond + italic year/label
                stacked vertically: drop-tick (24-30) → diamond (30-34) →
                label (38-47). Sits clear of the century numerals at bottom
                4-13 and the half-century / century ticks at 18-23. ── */}
          {TIMELINE_ANCHORS.map(({ year, label }) => {
            const x = yearToX(year);
            return (
              <div key={`anchor-${year}`} className="pointer-events-none">
                {/* Brass drop-tick from above the tick row up to the diamond. */}
                <div
                  className="absolute"
                  style={{
                    left: x,
                    bottom: 24,
                    width: 1,
                    height: 6,
                    background: "hsl(43, 50%, 45% / 0.55)",
                    transform: "translateX(-0.5px)",
                  }}
                />
                {/* Filled brass diamond at the tick's tip — the kind of
                    annotation flourish you'd expect on an antique chart. */}
                <div
                  className="absolute"
                  style={{
                    left: x,
                    bottom: 30,
                    width: 4,
                    height: 4,
                    background: "hsl(43, 60%, 50%)",
                    transform: "translate(-50%, 0) rotate(45deg)",
                    boxShadow: "0 0 4px hsl(var(--gold) / 0.4)",
                  }}
                />
                {/* Year + label — Cinzel year, italic Crimson label. Feels
                    handwritten next to the engraved century numerals. */}
                <span
                  className="absolute font-body italic whitespace-nowrap select-none"
                  style={{
                    left: x,
                    bottom: 38,
                    transform: "translateX(-50%)",
                    fontSize: "9px",
                    color: "hsl(var(--gold-dim) / 0.85)",
                    letterSpacing: "0.02em",
                    textShadow: "0 1px 0 hsl(var(--ink) / 0.5)",
                  }}
                >
                  <span style={{ fontStyle: "normal", fontFamily: "Cinzel, serif", letterSpacing: "0.05em" }}>{year}</span>
                  <span className="ml-1" style={{ color: "hsl(var(--gold-dim) / 0.6)" }}>{label}</span>
                </span>
              </div>
            );
          })}

          {/* ── Chronicle baseline — brass-dim hairline through the row band. ── */}
          <GoldRule
            tone="brass"
            intensity="soft"
            className="absolute"
            style={{
              left: 0,
              right: 0,
              top: BASE_TOP + Math.floor((maxRow + 1) / 2) * ROW_HEIGHT,
            }}
          />

          {/* ── Chronicle bars — parchment cartouches with region-color spine. ── */}
          {filtered.map((chronicle) => {
            const left = yearToX(chronicle.dateRange[0]);
            const width = Math.max((chronicle.dateRange[1] - chronicle.dateRange[0]) * PX_PER_YEAR, 22);
            const isHovered = hoveredChronicle === chronicle.id;
            const regionColor = REGIONS[chronicle.regionId as keyof typeof REGIONS]?.color || "hsl(43, 72%, 55%)";
            const row = rowAssignments.get(chronicle.id) ?? 0;
            const topOffset = BASE_TOP + row * ROW_HEIGHT;
            // Cross-component highlight: when the player hovers a region pin
            // on the map (or any bar in this timeline), bars whose region
            // doesn't match dim away so the focused region reads clearly.
            // The bar the cursor is directly on stays bright via `isHovered`.
            const isRegionMatched = focusedRegion === null || focusedRegion === chronicle.regionId;
            const isDimmedByRegion = !isRegionMatched && !isHovered;

            return (
              <div
                data-tutorial={chronicle.id === "carolingian-empire" ? "timeline-item-carolingian" : undefined}
                key={chronicle.id}
                className="absolute cursor-pointer"
                style={{
                  left,
                  top: topOffset,
                  width,
                  height: 24,
                  opacity: isDimmedByRegion ? 0.32 : 1,
                  transition: "opacity 0.18s ease-out",
                }}
                onPointerEnter={() => {
                  setHoveredChronicle(chronicle.id);
                  onRegionHoverChange?.(chronicle.regionId);
                }}
                onPointerLeave={() => {
                  setHoveredChronicle((prev) => (prev === chronicle.id ? null : prev));
                  onRegionHoverChange?.(null);
                }}
                onClick={() => onChronicleSelect(chronicle)}
              >
                <div
                  className="h-full rounded-sm overflow-hidden relative transition-all duration-150"
                  style={{
                    background: isHovered
                      ? `linear-gradient(180deg, ${withAlpha(regionColor, 0.42)}, ${withAlpha(regionColor, 0.22)})`
                      : "var(--surface-cartouche)",
                    border: `1px solid ${isHovered ? regionColor : "hsl(43, 50%, 35% / 0.32)"}`,
                    boxShadow: isHovered
                      ? `0 0 0 1px ${withAlpha(regionColor, 0.35)}, 0 0 14px ${withAlpha(regionColor, 0.3)}`
                      : "inset 0 1px 0 hsl(43, 50%, 50% / 0.05)",
                    filter: isHovered ? "brightness(1.08)" : "brightness(1)",
                  }}
                >
                  {/* Region-color spine — the at-rest cue for which region this belongs to. */}
                  <div
                    className="absolute left-0 top-0 bottom-0"
                    style={{
                      width: 3,
                      background: regionColor,
                      opacity: isHovered ? 1 : 0.85,
                      boxShadow: isHovered ? `0 0 6px ${withAlpha(regionColor, 0.5)}` : "none",
                    }}
                  />
                  <div className="absolute inset-0 pl-3 pr-1.5 flex items-center overflow-hidden">
                    <span
                      className="text-[10px] font-display whitespace-nowrap truncate tracking-wide"
                      style={{
                        color: isHovered ? "hsl(38, 30%, 92%)" : "hsl(38, 25%, 78%)",
                        textShadow: isHovered ? "0 1px 0 hsl(var(--ink) / 0.6)" : "0 1px 0 hsl(var(--ink) / 0.5)",
                      }}
                    >
                      {chronicle.title}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}

          {/* ── Hover popup (rendered at container level so it's never clipped) ── */}
          {hoveredChronicle && (() => {
            const chronicle = filtered.find(c => c.id === hoveredChronicle);
            if (!chronicle) return null;
            const left = yearToX(chronicle.dateRange[0]);
            const width = Math.max((chronicle.dateRange[1] - chronicle.dateRange[0]) * PX_PER_YEAR, 22);
            const row = rowAssignments.get(chronicle.id) ?? 0;
            const topOffset = BASE_TOP + row * ROW_HEIGHT;
            return (
              <ChroniclePopup
                chronicle={chronicle}
                barLeft={left}
                barTop={topOffset}
                barWidth={width}
                scrollContainerRef={scrollRef}
              />
            );
          })()}
        </div>
      </div>
    </div>
  );
}
